/**
 * AdminIntegrations — Phase 11
 *
 * Manage 3rd-party API keys (RentCast, ATTOM, Google OAuth, Stripe, OpenAI
 * override) via UI instead of editing .env. Keys are encrypted at rest with
 * Fernet (AES-256-GCM); the backend never returns the plaintext, only the
 * last 4 characters for fingerprinting.
 *
 * UX:
 *   - One card per provider with status pill + last_4
 *   - "Edit" opens a Dialog with masked input + Save / Test Connection / Clear
 *   - "Test all" runs a bulk health check in parallel
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  RefreshCw,
  ShieldAlert,
  Trash2,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = {
  configured: "border-emerald-200 bg-emerald-50 text-emerald-800",
  env_fallback: "border-amber-200 bg-amber-50 text-amber-800",
  not_set: "border-slate-200 bg-slate-50 text-slate-600",
};

const STATUS_LABEL = {
  configured: "Configured (DB)",
  env_fallback: "Configured via .env",
  not_set: "Not set",
};

function StatusPill({ status }) {
  const tone = STATUS_TONE[status] || STATUS_TONE.not_set;
  return (
    <span
      data-testid={`integration-status-${status}`}
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${tone}`}
    >
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function TestResult({ result }) {
  if (!result) return null;
  if (result.ok) {
    return (
      <div
        data-testid="integration-test-success"
        className="mt-3 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900"
      >
        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
        <div>
          <p className="font-semibold">Connection OK</p>
          <p className="opacity-70">
            Latency {result.latency_ms ?? "?"} ms
            {result.status_code ? ` · HTTP ${result.status_code}` : ""}
          </p>
          {result.note ? <p className="mt-1 opacity-70">{result.note}</p> : null}
        </div>
      </div>
    );
  }
  return (
    <div
      data-testid="integration-test-failure"
      className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-900"
    >
      <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
      <div>
        <p className="font-semibold">Connection failed</p>
        <p className="opacity-70">
          {result.error || "unknown error"}
          {result.status_code ? ` · HTTP ${result.status_code}` : ""}
          {result.latency_ms ? ` · ${result.latency_ms} ms` : ""}
        </p>
      </div>
    </div>
  );
}

function ProviderCard({ provider, onEdit, onTest, testing, lastTest }) {
  const Icon = provider.status === "configured" ? CheckCircle2
    : provider.status === "env_fallback" ? ShieldAlert
    : KeyRound;
  const iconTone = provider.status === "configured" ? "text-emerald-600"
    : provider.status === "env_fallback" ? "text-amber-600"
    : "text-slate-400";

  return (
    <div
      data-testid={`integration-card-${provider.provider}`}
      className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100">
            <Icon className={`h-5 w-5 ${iconTone}`} />
          </span>
          <div>
            <h3 className="text-sm font-semibold text-slate-900">{provider.label}</h3>
            <p className="mt-0.5 text-xs text-slate-500">{provider.description}</p>
            {provider.docs_url ? (
              <a
                href={provider.docs_url}
                target="_blank"
                rel="noreferrer noopener"
                className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
              >
                Get a key <ExternalLink className="h-3 w-3" />
              </a>
            ) : null}
          </div>
        </div>
        <StatusPill status={provider.status} />
      </div>

      <div className="mt-4 flex items-center gap-3 text-xs">
        <div className="flex-1">
          {provider.last_4 ? (
            <span className="font-mono text-slate-700">
              ••••<strong>{provider.last_4}</strong>
            </span>
          ) : (
            <span className="text-slate-400">No value</span>
          )}
          {provider.env_var ? (
            <span className="ml-2 text-slate-400">env var: {provider.env_var}</span>
          ) : null}
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onEdit(provider)}
          data-testid={`integration-edit-${provider.provider}`}
        >
          {provider.status === "not_set" ? "Add key" : "Edit"}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={provider.status === "not_set" || testing}
          onClick={() => onTest(provider.provider)}
          data-testid={`integration-test-${provider.provider}`}
        >
          {testing ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            "Test"
          )}
        </Button>
      </div>

      <TestResult result={lastTest} />
    </div>
  );
}

function EditDialog({ provider, open, onClose, onSaved }) {
  const [value, setValue] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (!open) {
      setValue("");
      setShow(false);
      setTestResult(null);
    }
  }, [open]);

  if (!provider) return null;

  const save = async () => {
    if (!value.trim()) {
      toast.error("Value cannot be empty");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/admin/integrations/${provider.provider}`, { value: value.trim() });
      toast.success(`${provider.label} key saved`);
      onSaved?.();
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post(`/admin/integrations/${provider.provider}/test`);
      setTestResult(data);
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || "request failed" });
    } finally {
      setTesting(false);
    }
  };

  const clearKey = async () => {
    if (provider.status !== "configured") {
      toast.error("No DB-stored key to clear (env fallback only)");
      return;
    }
    if (!window.confirm("Clear the DB-stored key? The .env fallback (if any) will be used.")) {
      return;
    }
    setDeleting(true);
    try {
      await api.delete(`/admin/integrations/${provider.provider}`);
      toast.success("Key cleared");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error("Failed to clear key");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="integration-edit-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{provider.label}</DialogTitle>
          <DialogDescription>{provider.description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {provider.status !== "not_set" ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">
              Current value: <span className="font-mono">••••{provider.last_4}</span>
              <span className="ml-2 opacity-70">
                ({provider.source === "db" ? "stored in DB" : "from .env"})
              </span>
            </div>
          ) : null}
          <div>
            <Label htmlFor="integration-value" className="text-slate-700">
              {provider.status === "not_set" ? "Paste new value" : "Replace with new value"}
            </Label>
            <div className="relative mt-1.5">
              <Input
                id="integration-value"
                data-testid="integration-value-input"
                type={show ? "text" : "password"}
                autoComplete="off"
                spellCheck="false"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={provider.status === "not_set" ? "Paste key here" : "Paste new key to replace"}
                className="pr-10 font-mono"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                aria-label={show ? "Hide" : "Show"}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-700"
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <p className="text-[11px] leading-relaxed text-slate-500">
            Stored encrypted at rest (Fernet AES-256-GCM). DB value takes precedence over the
            {provider.env_var ? ` ${provider.env_var}` : ""} env var. Deleting reverts to env.
          </p>
          <TestResult result={testResult} />
        </div>
        <DialogFooter className="flex flex-col gap-2 sm:flex-row">
          {provider.status === "configured" ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearKey}
              disabled={deleting}
              data-testid="integration-clear"
              className="text-red-600 hover:text-red-800"
            >
              {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (
                <><Trash2 className="mr-1 h-3.5 w-3.5" /> Clear DB key</>
              )}
            </Button>
          ) : null}
          <div className="flex flex-1 items-center justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={testing || (provider.status === "not_set" && !value.trim())}
              onClick={test}
              data-testid="integration-test-connection"
            >
              {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Test connection"}
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={saving || !value.trim()}
              onClick={save}
              data-testid="integration-save"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminIntegrations() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editProvider, setEditProvider] = useState(null);
  const [testing, setTesting] = useState({});
  const [testResults, setTestResults] = useState({});
  const [bulkTesting, setBulkTesting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/integrations");
      setData(data);
    } catch (e) {
      toast.error("Failed to load integrations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const test = useCallback(async (provider) => {
    setTesting((t) => ({ ...t, [provider]: true }));
    try {
      const { data } = await api.post(`/admin/integrations/${provider}/test`);
      setTestResults((r) => ({ ...r, [provider]: data }));
    } catch (e) {
      setTestResults((r) => ({ ...r, [provider]: { ok: false, error: "request failed" } }));
    } finally {
      setTesting((t) => ({ ...t, [provider]: false }));
    }
  }, []);

  const testAll = async () => {
    setBulkTesting(true);
    try {
      const { data } = await api.post("/admin/integrations/test-all");
      setTestResults((r) => ({ ...r, ...data.providers }));
      const okCount = Object.values(data.providers || {}).filter((p) => p.ok).length;
      const total = Object.keys(data.providers || {}).length;
      if (okCount === total) {
        toast.success(`All ${total} integrations healthy`);
      } else {
        toast.warning(`${okCount}/${total} integrations healthy`);
      }
    } catch (e) {
      toast.error("Bulk test failed");
    } finally {
      setBulkTesting(false);
    }
  };

  if (!data && loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="admin-integrations-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading integrations…
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6 fade-in" data-testid="admin-integrations">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">Settings</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900">Integrations</h1>
          <p className="mt-1 text-sm text-slate-500">
            Manage encrypted API keys. DB values override <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700">.env</code> at runtime.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="admin-integrations-refresh">
            <RefreshCw className={`mr-1 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={testAll} disabled={bulkTesting} data-testid="admin-integrations-test-all">
            {bulkTesting ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
            Test all
          </Button>
        </div>
      </div>

      {!data.master_key_configured ? (
        <div
          data-testid="master-key-warning"
          className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
          <div>
            <p className="font-semibold">INTEGRATIONS_MASTER_KEY is missing</p>
            <p className="mt-1 text-xs opacity-80">
              Encrypted storage is unavailable — saving a key will fail with HTTP 503. The system
              falls back to <code className="rounded bg-white/60 px-1">.env</code> values for now.
              Add a Fernet key to backend secrets and restart.
            </p>
          </div>
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        {data.providers.map((p) => (
          <ProviderCard
            key={p.provider}
            provider={p}
            onEdit={setEditProvider}
            onTest={test}
            testing={!!testing[p.provider]}
            lastTest={testResults[p.provider]}
          />
        ))}
      </div>

      <EditDialog
        provider={editProvider}
        open={!!editProvider}
        onClose={() => setEditProvider(null)}
        onSaved={load}
      />
    </div>
  );
}
